import * as pdfjsLib from 'pdfjs-dist';
import JSZip from 'jszip';
import { encodeCanvas, extForBlob } from '../../image/shared/imageOutputFormats';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

const DPI_SCALE = {
    normal: 96 / 72,
    high: 144 / 72,
    ultra: 216 / 72,
};

const EXT_BY_FORMAT = {
    jpg: 'jpg',
    png: 'png',
    webp: 'webp',
    avif: 'avif',
};

const MIME_BY_FORMAT = {
    jpg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    avif: 'image/avif',
};

const QUALITY_BY_PRESET = {
    normal: 0.85,
    high: 0.9,
    ultra: 0.92,
};

async function renderPageToBlob(page, scale, format, quality) {
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext('2d', { alpha: format === 'png' });
    if (format !== 'png') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    await page.render({ canvasContext: ctx, viewport, background: '#ffffff' }).promise;

    // Shared encoder (image tools use the same): Chrome's canvas cannot write
    // AVIF — `toBlob('image/avif')` quietly hands back a PNG, which used to ship
    // named `.avif` — so AVIF is written in WebAssembly there instead.
    const { blob } = await encodeCanvas(canvas, MIME_BY_FORMAT[format] ? format : 'jpg', { quality });
    return blob;
}

async function loadPdf(file) {
    const data = new Uint8Array(await file.arrayBuffer());
    try {
        return await pdfjsLib.getDocument({ data }).promise;
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('password')) {
            throw new Error('password');
        }
        throw err;
    }
}

function imageName(file, pageNum, pageTotal, fileTotal, ext) {
    const base = file.name.replace(/\.pdf$/i, '');
    if (fileTotal > 1 || pageTotal > 1) return `${base}-page-${pageNum}.${ext}`;
    return `${base}.${ext}`;
}

/**
 * Convert one or more PDFs to images (one image per page).
 * @param {File[]} files
 * @param {{ format: string, quality: string }} options
 * @param {(page: number, total: number) => void} [onProgress]
 * @returns {Promise<{ name: string, blob: Blob }[]>}
 */
export async function convertPdfsToImages(files, options, onProgress) {
    const format = options.format || 'jpg';
    const scale = DPI_SCALE[options.quality] || DPI_SCALE.normal;
    const jpegQuality = QUALITY_BY_PRESET[options.quality] || QUALITY_BY_PRESET.normal;

    const loaded = [];
    for (const file of files) {
        loaded.push({ file, pdf: await loadPdf(file) });
    }
    const totalPages = loaded.reduce((sum, item) => sum + item.pdf.numPages, 0);

    const images = [];
    let done = 0;

    for (const { file, pdf } of loaded) {
        const pageTotal = pdf.numPages;
        for (let i = 1; i <= pageTotal; i++) {
            const page = await pdf.getPage(i);
            const blob = await renderPageToBlob(page, scale, format, jpegQuality);
            done += 1;
            onProgress?.(done, totalPages);

            // The extension follows what was actually written (a WebAssembly
            // failure falls back to WebP), never what was asked for.
            images.push({
                name: imageName(file, i, pageTotal, files.length, extForBlob(blob, EXT_BY_FORMAT[format] || 'jpg')),
                blob,
            });
        }
    }

    return images;
}

/** @param {{ name: string, blob: Blob }[]} items */
/**
 * Zip a list of blobs, giving repeated names a suffix.
 *
 * Two entries with the same name are legal in a zip file but most extractors
 * keep only one, so a duplicated page — or a copied image cropped twice —
 * silently vanished from the download.
 */
export async function zipImageFiles(items) {
    const zip = new JSZip();
    const used = new Set();

    for (const item of items) {
        const dot = item.name.lastIndexOf('.');
        const base = dot > 0 ? item.name.slice(0, dot) : item.name;
        const ext = dot > 0 ? item.name.slice(dot) : '';

        let name = item.name;
        let n = 1;
        while (used.has(name)) {
            n += 1;
            name = `${base} (${n})${ext}`;
        }
        used.add(name);
        zip.file(name, item.blob);
    }

    return zip.generateAsync({ type: 'blob' });
}
